import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./components/Layout";
import { useAuth } from "./auth";
import { LoginPage } from "./pages/Login";
import { RegisterPage } from "./pages/Register";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import {
  LoginLinkExchangePage,
  LoginLinkRequestPage,
} from "./pages/LoginLinkPage";
import { ClaimUnitPage } from "./pages/ClaimUnitPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectPage } from "./pages/ProjectPage";
import { NewProjectPage } from "./pages/NewProjectPage";
import { ManagementPage } from "./pages/ManagementPage";
import { BoardPage } from "./pages/BoardPage";
import { InspectQueue } from "./pages/inspect/InspectQueue";
import { InspectProject } from "./pages/inspect/InspectProject";
import { MyInsurancePage } from "./pages/MyInsurancePage";
import { MyCompliancePage } from "./pages/MyCompliancePage";
import { HelpPage } from "./pages/HelpPage";
import { ContractorHelpPage } from "./pages/ContractorHelpPage";
import { ContractorDirectoryPage } from "./pages/ContractorDirectoryPage";
import { ContractorExchangePage } from "./pages/ContractorExchangePage";
import { ContractorPortalPage } from "./pages/ContractorPortalPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminBuildings } from "./pages/admin/AdminBuildings";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminUnits } from "./pages/admin/AdminUnits";
import { AdminRisers } from "./pages/admin/AdminRisers";
import { AdminProjectTypes } from "./pages/admin/AdminProjectTypes";
import { AdminInvites } from "./pages/admin/AdminInvites";
import { AdminClaims } from "./pages/admin/AdminClaims";
import { AdminElmMetrics } from "./pages/admin/AdminElmMetrics";
import { AdminUserInsurance } from "./pages/admin/AdminUserInsurance";
import { AdminUserDetail } from "./pages/admin/AdminUserDetail";
import { AdminUserEdit } from "./pages/admin/AdminUserEdit";
import { AdminUnitDetail } from "./pages/admin/AdminUnitDetail";
import { AdminInsuranceReview } from "./pages/admin/AdminInsuranceReview";
import { AdminContractorImport } from "./pages/admin/AdminContractorImport";
import { ProfilePage } from "./pages/me/ProfilePage";
import { SettingsPage } from "./pages/me/SettingsPage";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  if (state.status === "loading") return <p className="muted">Loading…</p>;
  if (state.status === "anonymous") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  const { state } = useAuth();
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          path="/construction"
          element={
            state.status === "authenticated" ? (
              <Navigate to="/projects" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/" element={<Navigate to="/construction" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/login-link" element={<LoginLinkRequestPage />} />
        <Route path="/login/link" element={<LoginLinkExchangePage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/claim-unit" element={<ClaimUnitPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/contractor" element={<ContractorHelpPage />} />
        <Route path="/contractor/exchange" element={<ContractorExchangePage />} />
        <Route path="/contractor/portal" element={<ContractorPortalPage />} />

        <Route
          path="/projects"
          element={
            <RequireAuth>
              <ProjectsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/projects/new"
          element={
            <RequireAuth>
              <NewProjectPage />
            </RequireAuth>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <RequireAuth>
              <ProjectPage />
            </RequireAuth>
          }
        />
        <Route
          path="/contractors"
          element={
            <RequireAuth>
              <ContractorDirectoryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/my/insurance"
          element={
            <RequireAuth>
              <MyInsurancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/my/compliance"
          element={
            <RequireAuth>
              <MyCompliancePage />
            </RequireAuth>
          }
        />
        <Route
          path="/me/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />
        <Route
          path="/me/settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />

        <Route
          path="/management"
          element={
            <RequireAuth>
              <ManagementPage />
            </RequireAuth>
          }
        />
        <Route
          path="/board"
          element={
            <RequireAuth>
              <BoardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/inspect"
          element={
            <RequireAuth>
              <InspectQueue />
            </RequireAuth>
          }
        />
        <Route
          path="/inspect/:id"
          element={
            <RequireAuth>
              <InspectProject />
            </RequireAuth>
          }
        />

        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/admin/buildings" replace />} />
          <Route path="buildings" element={<AdminBuildings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="units" element={<AdminUnits />} />
          <Route path="risers" element={<AdminRisers />} />
          <Route path="project-types" element={<AdminProjectTypes />} />
          <Route path="invites" element={<AdminInvites />} />
          <Route path="claims" element={<AdminClaims />} />
          <Route path="insurance-review" element={<AdminInsuranceReview />} />
          <Route path="contractor-import" element={<AdminContractorImport />} />
          <Route path="elm-metrics" element={<AdminElmMetrics />} />
          <Route
            path="users/:userId/insurance"
            element={<AdminUserInsurance />}
          />
          <Route path="users/:userId/edit" element={<AdminUserEdit />} />
          <Route path="users/:userId" element={<AdminUserDetail />} />
          <Route path="units/:unitId" element={<AdminUnitDetail />} />
        </Route>
      </Route>
    </Routes>
  );
}
